import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AfterContentChecked, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, AfterViewInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Direction } from '@angular/cdk/bidi';
import { AuthService } from './auth/auth.service';
import { LoaderService } from './shared/components/loader/loader.service';
import { TourService } from 'ngx-ui-tour-md-menu';
import { GuidelineService } from './shared/service/guideline.service';
import { Title, Meta } from '@angular/platform-browser';
import { StructuredDataService } from './shared/service/structured-data.service';
import { ThemeService } from './pages/theme/services/theme.service';
import { PwaDebugService } from './shared/service/pwa-debug.service';
import { PwaService } from './shared/service/pwa.service';
import { Router, NavigationEnd } from '@angular/router';
import { environment } from '../environments/environment';
import { GeolocationService } from './shared/service/geolocation.service';




@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],

})
export class AppComponent implements OnDestroy, OnInit, AfterContentChecked, AfterViewInit {

  // TODO: uncomment this

  // @HostListener('window:beforeunload', ['$event'])
  // beforeunloadHandler(event) {
  //   event.preventDefault();
  //   event.returnValue = 'app Your data will be lost!';
  //   return false;
  // }

  private subscriptions = new Subscription();
  direction: Direction;
  currentLang: string;
  isOnSupportPage = false; // Track if we're on pages that should show visitor chat floating button

  constructor(
    private cdref: ChangeDetectorRef,
    public _translate: TranslateService,
    private _auth: AuthService,
    private guidelineService: GuidelineService,
    public loader: LoaderService,
    private titleService: Title,
    private metaService: Meta,
    private structuredDataService: StructuredDataService,
    private themeService: ThemeService,
    private pwaDebugService: PwaDebugService,
    private pwaService: PwaService,
    private router: Router,
    private geolocationService: GeolocationService
  ) {

    this._auth.loadPackages();

    this.setInitLanguage();
    this.subscriptions.add(
      this._translate.onLangChange.subscribe((res) => {
        this.direction = res.lang === 'ar' ? 'rtl' : 'ltr';
        // Ensure global styles that depend on body[dir] update immediately
        document.body.setAttribute('dir', this.direction);
        //localStorage.removeItem('CONFIG');
        this.currentLang = this._translate.currentLang;
        this._auth.requestConfigData();
        // Update page metadata when language changes
        this.updatePageMetadata();
      })
    );

    // Track route changes to set unlimited usage on support page
    this.subscriptions.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe((event: NavigationEnd) => {
        this.isOnSupportPage = event.url.includes('/pages/support') || event.url.includes('/index') || event.url === '/';
      })
    );

    // Set initial page metadata
    this.updatePageMetadata();
  }

  /**
   * Apply any saved theme from localStorage when the app initializes
   * This ensures the custom theme is applied even before API calls complete
   */
  applyStoredTheme(): void {
    try {
      const storedTheme = localStorage.getItem('theme');
      if (storedTheme) {
        const themeData = JSON.parse(storedTheme);
        this.themeService.applyCustomerTheme(themeData);
        console.log('Applied stored theme from localStorage on app startup');
      } else {
        console.log('No stored theme found in localStorage');
      }
    } catch (error) {
      console.error('Error applying stored theme:', error);
    }
  }

  ngOnInit() {
    // Apply theme from localStorage early in application lifecycle
    this.applyStoredTheme();

    // Initialize support page detection
    this.isOnSupportPage = this.router.url.includes('/pages/support') || this.router.url.includes('/index') || this.router.url === '/';

    // PWA debugging for production environment
    if (environment.production && (environment as any).pwa?.enabled) {
      setTimeout(() => {
        this.pwaDebugService.debugPwaInstallability();
        this.pwaDebugService.checkInstallCriteria();
      }, 3000); // Wait 3 seconds for everything to load
    }

    // to update firebase token from mobile app
    document.addEventListener(
      'updateNotificationToken',
      function (e) {
        console.log(' mobile token is >>>', e.detail); // Prints "Example of an event"
        const req = { token: e.detail };
        this.updateNotificationToken(req);
      }.bind(this)
    );
    window.addEventListener('mouseup', function (event) {
      const notificationIcon = document.getElementById('notificationIconId');
      const notificationContent = document.getElementById('notificationContentId');
      if (event.target != notificationIcon) {
        if (event.target != notificationContent) {
          notificationContent?.classList?.remove('showNotificationContent');
        }
      }
    });

    // Add structured data for SEO
    this.addStructuredData();
  }
  public tourService = inject(TourService);

  updateNotificationToken(req) {
    const accessToken = localStorage.getItem('ACCESS_TOKEN');
    if (!accessToken) {
      return;
    }
    this._auth.updateNotificationToken(req).subscribe((res) => {
      console.log(res)
    });
  }
  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }

  ngAfterViewInit() {
    // Mark the body with chat-present class to correctly position floating buttons
    setTimeout(() => {
      document.body.classList.add('chat-present');
    }, 1000);
  }

  /**
   * Updates page title and meta tags for SEO
   */  private updatePageMetadata(): void {
    const baseTitle = this._translate.instant('APP.TITLE') || 'loading...';
    const description = this._translate.instant('APP.DESCRIPTION') || 'Khdma provides professional services for all your needs. Find reliable services, expert providers, and affordable solutions.';
    const keywords = this._translate.instant('APP.KEYWORDS') || 'khdma, professional services, service provider';

    // Only set title if translation is available, otherwise keep "loading..."
    if (this._translate.instant('APP.TITLE') !== 'APP.TITLE') {
      this.titleService.setTitle(baseTitle);
    }

    // Update meta tags
    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({ name: 'keywords', content: keywords });

    // Set Open Graph tags for social sharing
    this.metaService.updateTag({ property: 'og:title', content: baseTitle });
    this.metaService.updateTag({ property: 'og:description', content: description });
    this.metaService.updateTag({ property: 'og:type', content: 'website' });

    // Set robots meta tag to ensure indexing
    this.metaService.updateTag({ name: 'robots', content: 'index, follow' });
  }

  /**
   * Adds structured data to the page for better SEO
   */
  private addStructuredData(): void {
    const name = this._translate.instant('APP.ORGANIZATION_NAME') || 'Khdma';
    const description = this._translate.instant('APP.DESCRIPTION') || 'Khdma provides professional services for all your needs.';
    console.log(description)

    // Add Organization schema
    this.structuredDataService.addOrganizationSchema({
      name: name,
      url: window.location.origin,
      logo: `${window.location.origin}/assets/images/logo.png`,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service'
      }
    });
  }

  /** Languages supported by the system (see CustomTranslateLoader). */
  private readonly SUPPORTED_LANGS = ['ar', 'en', 'fr', 'es'];

  /** Fallback language when detection fails or the country/locale is unknown. */
  private readonly DEFAULT_LANG = 'en';

  /**
   * Map of ISO-3166 country code -> ordered preferred languages [primary, secondary].
   * Only languages the system supports (ar/en/fr/es) are listed. Any country not
   * present here falls back to the system default ('ar').
   */
  private readonly COUNTRY_LANG: Record<string, string[]> = {
    // Arabic-primary countries
    SA: ['ar'], EG: ['ar'], AE: ['ar'], QA: ['ar'], KW: ['ar'], BH: ['ar'],
    OM: ['ar'], JO: ['ar'], LB: ['ar'], SY: ['ar'], IQ: ['ar'], YE: ['ar'],
    PS: ['ar'], SD: ['ar'], LY: ['ar'],
    // Maghreb: Arabic primary, French secondary
    DZ: ['ar', 'fr'], MA: ['ar', 'fr'], TN: ['ar', 'fr'], MR: ['ar', 'fr'],
    // French-primary countries
    FR: ['fr'], MC: ['fr'], LU: ['fr'], BE: ['fr'],
    CA: ['en', 'fr'],
    // Spanish-primary countries
    ES: ['es'], MX: ['es'], AR: ['es'], CO: ['es'], PE: ['es'], CL: ['es'],
    VE: ['es'], EC: ['es'], GT: ['es'], CU: ['es'], BO: ['es'], DO: ['es'],
    HN: ['es'], PY: ['es'], SV: ['es'], NI: ['es'], CR: ['es'], PA: ['es'], UY: ['es'],
    // English-primary countries
    US: ['en'], GB: ['en'], AU: ['en'], NZ: ['en'], IE: ['en'], IN: ['en'],
    PK: ['en'], NG: ['en'], ZA: ['en'], PH: ['en'], SG: ['en'],
  };

  /** Set when the user manually picks a language, to cancel async auto-detection. */
  private languageManuallySelected = false;

  setInitLanguage() {
    const savedLang = localStorage.getItem('LANG');
    if (savedLang && savedLang.length) {
      // Returning user: always respect the persisted choice, no detection.
      this.applyLanguage(savedLang);
      return;
    }

    // First visit: apply an instant client-side guess so the app never waits,
    // then refine it (non-blocking) using accurate IP-based country detection.
    const instant = this.detectLanguageFromBrowser();
    localStorage.setItem('LANG', instant);
    this.applyLanguage(instant);
    this.detectLanguageFromCountry();
  }

  /** Apply a language in-place (no page reload, no spinner). */
  private applyLanguage(lang: string) {
    this._translate.setDefaultLang(lang);
    this._translate.use(lang);
    this.currentLang = lang;
    this.direction = lang === 'ar' ? 'rtl' : 'ltr';
    document.body.setAttribute('dir', this.direction);
  }

  /** Synchronous, zero-network guess from the browser's locale preferences. */
  private detectLanguageFromBrowser(): string {
    const prefs = (navigator.languages && navigator.languages.length)
      ? navigator.languages
      : [navigator.language || this.DEFAULT_LANG];
    for (const tag of prefs) {
      if (!tag) continue;
      const parts = tag.split('-');
      const lang = (parts[0] || '').toLowerCase();
      const region = (parts[1] || '').toUpperCase();
      // Respect the user's own language preference order first.
      if (this.SUPPORTED_LANGS.includes(lang)) return lang;
      // Otherwise fall back to the region's primary/secondary language.
      const byCountry = this.languageByCountry(region);
      if (byCountry) return byCountry;
    }
    return this.DEFAULT_LANG;
  }

  /**
   * Accurate, non-blocking refinement using the existing GeolocationService
   * (IP-based, cached once per session). Overrides the instant guess only if
   * the user has not manually chosen a language in the meantime.
   *
   * IMPORTANT: a failed/unknown IP lookup (the service returns country_code
   * 'XX' / 'Unknown' through the success path, e.g. when the external IP
   * services are blocked or rate-limited on localhost) must NOT downgrade the
   * instant guess to the default language. We only switch when a real, mapped
   * country is detected.
   */
  private detectLanguageFromCountry(): void {
    this.geolocationService.getCountryByIP().subscribe({
      next: (loc) => {
        if (this.languageManuallySelected) return;
        const code = (loc?.country_code || '').toUpperCase();
        // Unknown / failed detection -> keep the instant guess as-is.
        if (!code || code === 'XX' || code === 'UNKNOWN') return;
        const lang = this.languageByCountry(code);
        // Country not in our map -> keep the instant guess (don't force default).
        if (!lang) return;
        if (lang !== this.currentLang) {
          localStorage.setItem('LANG', lang);
          this.applyLanguage(lang);
        }
      },
      error: () => { /* keep the instant guess on failure */ }
    });
  }

  /**
   * Resolve a country code to a supported language using its [primary, secondary]
   * mapping. The secondary language is used as an intelligent tiebreaker: when the
   * country has more than one supported language, the user's own browser language
   * preferences decide which one wins (e.g. a French-preferring visitor in Morocco
   * gets 'fr' instead of the primary 'ar'). Falls back to the primary otherwise.
   */
  private languageByCountry(countryCode: string): string | null {
    if (!countryCode) return null;
    const langs = (this.COUNTRY_LANG[countryCode] || []).filter(l => this.SUPPORTED_LANGS.includes(l));
    if (!langs.length) return null;
    if (langs.length === 1) return langs[0];

    // Multiple supported languages for this country (primary + secondary):
    // let the browser's preference order break the tie.
    const browserLangs = this.getBrowserLanguageOrder();
    for (const pref of browserLangs) {
      if (langs.includes(pref)) return pref;
    }
    // No browser hint matched -> use the country's primary language.
    return langs[0];
  }

  /** Ordered list of the user's supported browser languages (deduplicated). */
  private getBrowserLanguageOrder(): string[] {
    const prefs = (navigator.languages && navigator.languages.length)
      ? navigator.languages
      : [navigator.language || ''];
    const order: string[] = [];
    for (const tag of prefs) {
      if (!tag) continue;
      const lang = (tag.split('-')[0] || '').toLowerCase();
      if (this.SUPPORTED_LANGS.includes(lang) && !order.includes(lang)) {
        order.push(lang);
      }
    }
    return order;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  end() {
    this.guidelineService.end()
  }

  changeLanguage(lang: string) {
    this.languageManuallySelected = true;
    this._translate.use(lang);
    localStorage.setItem('LANG', lang);
    location.reload();
  }
}
